import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ModerationResource,
  ModerationConfig,
  DeploymentStatus,
} from "../../api/moderation";
import { useModerationStore } from "../../store/moderationStore";
import { useModerationApi } from "./hooks/useModerationApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useDialogState } from "../../hooks/useDialogState";
import { useSnackbar } from "../../hooks/useSnackbar";
import { isValidThreshold, isValidTimeout } from "./utils";

interface ThresholdsSectionProps {
  onRefresh: () => void;
}

export const ThresholdsSection: React.FC<ThresholdsSectionProps> = ({
  onRefresh,
}) => {
  const api = useModerationApi();
  const { withLoading } = useGlobalLoading();
  const { configResponse } = useModerationStore();

  const [formData, setFormData] = useState<ModerationConfig | null>(
    configResponse?.config || null
  );
  const [deployment, setDeployment] = useState<DeploymentStatus | null>(
    configResponse?.deployment || null
  );
  const [isPolling, setIsPolling] = useState(false);
  const confirmDialog = useDialogState();
  const { showSnackbar, hideSnackbar, snackbar } = useSnackbar();

  // Update form data when store data changes
  useEffect(() => {
    if (configResponse) {
      setFormData(configResponse.config);
      setDeployment(configResponse.deployment);
    }
  }, [configResponse]);

  // Poll for deployment status after save
  useEffect(() => {
    if (!isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const result = await api.getConfig();
        setDeployment(result.deployment);

        if (result.deployment.state === "COMPLETE") {
          setIsPolling(false);
        }
      } catch (error) {
        console.error("Failed to poll deployment status:", error);
        setIsPolling(false);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isPolling, api]);

  const handleChange = (field: string, value: any) => {
    if (!formData) return;
    setFormData((prev: ModerationConfig | null) =>
      prev ? { ...prev, [field]: value } : prev
    );
  };

  const handleCategoryChange = (
    category: string,
    field: string,
    value: any
  ) => {
    if (!formData) return;
    setFormData((prev: ModerationConfig | null) =>
      prev
        ? {
            ...prev,
            categories: {
              ...prev.categories,
              [category]: {
                ...(prev.categories?.[
                  category as keyof typeof prev.categories
                ] || {}),
                [field]: value,
              },
            },
          }
        : prev
    );
  };

  const handleApplyClick = () => {
    if (!formData) return;

    if (!isValidThreshold(formData.deleteThreshold)) {
      showSnackbar("Delete threshold must be between 0.0 and 1.0", "error");
      return;
    }

    if (!isValidThreshold(formData.flagThreshold)) {
      showSnackbar("Flag threshold must be between 0.0 and 1.0", "error");
      return;
    }

    if (!isValidTimeout(formData.timeoutMs)) {
      showSnackbar("Timeout must be between 1000 and 30000 ms", "error");
      return;
    }

    confirmDialog.openDialog();
  };

  const handleConfirmApply = async () => {
    if (!formData) return;
    confirmDialog.close();

    try {
      await withLoading(api.updateConfig(formData));
      showSnackbar("Configuration updated successfully", "success");
      setIsPolling(true);
      onRefresh();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to update configuration", "error");
    }
  };

  const handleCancelApply = () => {
    confirmDialog.close();
  };

  const renderDeploymentStatus = () => {
    if (!deployment) return null;

    switch (deployment.state) {
      case "COMPLETE":
        return <Chip label="Deployed" color="success" size="small" />;
      case "DEPLOYING":
        return (
          <Chip
            label="Deploying..."
            color="warning"
            size="small"
            icon={<CircularProgress size={14} color="inherit" />}
          />
        );
      case "BAKING":
        return (
          <Chip
            label="Baking..."
            color="info"
            size="small"
            icon={<CircularProgress size={14} color="inherit" />}
          />
        );
      case "ROLLING_BACK":
      case "ROLLED_BACK":
        return <Chip label={deployment.state} color="error" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  if (!formData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">Moderation Thresholds</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Status:
          </Typography>
          {renderDeploymentStatus()}
        </Box>
      </Box>

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ mb: 2 }}
          onClose={hideSnackbar}
        >
          {snackbar.message}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.enabled}
              onChange={(e) => handleChange("enabled", e.target.checked)}
            />
          }
          label="Enable Moderation"
        />

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Delete Threshold"
              type="number"
              value={formData.deleteThreshold}
              onChange={(e) =>
                handleChange("deleteThreshold", parseFloat(e.target.value))
              }
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="0.0 - 1.0 (confidence threshold to delete messages)"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Flag Threshold"
              type="number"
              value={formData.flagThreshold}
              onChange={(e) =>
                handleChange("flagThreshold", parseFloat(e.target.value))
              }
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="0.0 - 1.0 (confidence threshold to flag messages)"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Timeout (ms)"
              type="number"
              value={formData.timeoutMs}
              onChange={(e) =>
                handleChange("timeoutMs", parseInt(e.target.value))
              }
              inputProps={{ min: 1000, max: 30000, step: 1000 }}
              helperText="1000 - 30000 ms"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Language"
              value={formData.language}
              onChange={(e) => handleChange("language", e.target.value)}
              helperText="Language code (e.g., 'en')"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SEXUAL Category */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          SEXUAL Category Thresholds
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Delete Threshold"
              type="number"
              value={formData.categories?.SEXUAL?.deleteThreshold || 0}
              onChange={(e) =>
                handleCategoryChange(
                  "SEXUAL",
                  "deleteThreshold",
                  parseFloat(e.target.value)
                )
              }
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="0.0 - 1.0"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Flag Threshold"
              type="number"
              value={formData.categories?.SEXUAL?.flagThreshold || 0}
              onChange={(e) =>
                handleCategoryChange(
                  "SEXUAL",
                  "flagThreshold",
                  parseFloat(e.target.value)
                )
              }
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="0.0 - 1.0"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={handleApplyClick}
          sx={{
            backgroundColor: "#7357FF",
            "&:hover": { backgroundColor: "#5a42cc" },
          }}
        >
          Apply
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancelApply}>
        <DialogTitle>Confirm Configuration Apply</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to apply these changes? The AppConfig will be
            deployed to the current environment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelApply}>Cancel</Button>
          <Button
            onClick={handleConfirmApply}
            variant="contained"
            sx={{
              backgroundColor: "#7357FF",
              "&:hover": { backgroundColor: "#5a42cc" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
